"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { localePath, routes } from "@/lib/routes";
import { useEmailOtp, type EmailOtpEntityType } from "@/components/quick-flow/ergaenzen/useEmailOtp";
import { OtpDeliveryNotice } from "@/components/quick-flow/ergaenzen/OtpDeliveryNotice";
interface ChatOtpCardProps {
  entityId: string;
  entityType: EmailOtpEntityType;
  /** Pre-filled email if available. Empty string puts the card into edit mode. */
  initialEmail?: string;
  /** Already-verified state — skips the card entirely if true. */
  initialVerified?: boolean;
  onVerified?: () => void;
}

/**
 * Why the user is being asked, keyed by entity type.
 *
 * NOT "Ihr Angebot ist noch nicht aktiv". That headline inverted the ask: it
 * announced an internal state the user never asked about — a row exists, it is
 * inactive — and buried the one thing they can do about it. Creating the row
 * early is OUR mechanism for not losing the lead (see lib/chat/flow-progress.ts);
 * the user's task is the ordinary one, confirming an email address, and
 * activation is our consequence of it. Prod c6efd18e is what the old framing
 * cost: an alarm the user could not act on, four turns above where they were
 * looking.
 */
const REASONS: Record<EmailOtpEntityType, string> = {
  offering: "Danach melden wir uns bei Ihnen, sobald passende Käufer zu Ihrem Angebot vorliegen.",
  request: "Danach melden wir uns bei Ihnen, sobald passende Verkäufer zu Ihrer Anfrage vorliegen."
};

/**
 * Post-verification success copy, keyed by entity type.
 *
 * No liveness claim ("ist aktiv", "sucht ab jetzt"): confirmation lands the row
 * on `preliminary`, which is not matched (MATCHABLE_*_STATUSES in
 * lib/matching/types.ts), and since the card shows from the draft's creation on
 * it is usually confirmed while Menge, Preis or Budget are still missing.
 */
const SUCCESS_COPY: Record<EmailOtpEntityType, {
  title: string;
  body: string;
  /** Label for the entity-details follow-up CTA. */
  followUpLabel: string;
  /** Build the absolute path the CTA links to. */
  followUpHref: (id: string) => string;
}> = {
  offering: {
    title: "E-Mail bestätigt",
    body: "Danke! Ihr Angebot ist gespeichert — fehlende Angaben können Sie jederzeit ergänzen.",
    followUpLabel: "Weitere Details ergänzen",
    followUpHref: id => localePath(routes.offerings.edit(id))
  },
  request: {
    title: "E-Mail bestätigt",
    body: "Danke! Ihre Anfrage ist gespeichert — fehlende Angaben können Sie jederzeit ergänzen.",
    followUpLabel: "Weitere Details ergänzen",
    followUpHref: id => localePath(routes.requests.edit(id))
  }
};

/**
 * Chat-tuned email-OTP card. Used inline in the chatbot bubble after
 * `create_offering_draft` / `create_request_draft` returns success.
 *
 * Why this exists alongside `<OtpVerificationCard>`:
 *   - chat bubble is ~240px wide; 6 separate OTP boxes overflow
 *   - the standalone card's boxed "code-sent" banner wraps to many
 *     short lines at this width
 *   - on mobile, a single numeric input gets the native one-time-code
 *     keyboard treatment (autofill, smart-bar) that 6 inputs don't
 *
 * Both components share state + API via `useEmailOtp`.
 */
export function ChatOtpCard(t0) {
  const $ = _c(33);
  const {
    entityId,
    entityType,
    initialEmail: t1,
    initialVerified: t2,
    onVerified
  } = t0;
  const initialEmail = t1 === undefined ? "" : t1;
  const initialVerified = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== entityId || $[1] !== entityType || $[2] !== initialEmail || $[3] !== initialVerified || $[4] !== onVerified) {
    t3 = {
      entityId,
      entityType,
      initialEmail,
      initialVerified,
      onVerified
    };
    $[0] = entityId;
    $[1] = entityType;
    $[2] = initialEmail;
    $[3] = initialVerified;
    $[4] = onVerified;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const otp = useEmailOtp(t3);
  if (otp.verified) {
    const copy = SUCCESS_COPY[entityType];
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <span aria-hidden={true}>✓</span>;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== copy.title) {
      t5 = <p className="text-sm font-extrabold text-emerald-900 flex items-center gap-2">{t4}{copy.title}</p>;
      $[7] = copy.title;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== copy.body) {
      t6 = <p className="mt-1 text-xs text-emerald-900/80 leading-snug">{copy.body}</p>;
      $[9] = copy.body;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== copy || $[12] !== entityId) {
      t7 = copy.followUpHref(entityId);
      $[11] = copy;
      $[12] = entityId;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <span aria-hidden={true}>→</span>;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== copy.followUpLabel || $[16] !== t7) {
      t9 = <Link href={t7} className="mt-3 inline-flex items-center justify-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs font-bold transition-colors no-underline">{copy.followUpLabel}{t8}</Link>;
      $[15] = copy.followUpLabel;
      $[16] = t7;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    let t10;
    if ($[18] !== t5 || $[19] !== t6 || $[20] !== t9) {
      t10 = <div className="rounded-2xl border border-emerald-300/60 bg-emerald-50/70 p-3 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300">{t5}{t6}{t9}</div>;
      $[18] = t5;
      $[19] = t6;
      $[20] = t9;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    return t10;
  }
  let t4;
  if ($[22] !== otp.code) {
    t4 = otp.code.join("");
    $[22] = otp.code;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const codeString = t4;
  let t5;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="text-sm font-extrabold text-primary">E-Mail-Adresse bestätigen</p>;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const t6 = REASONS[entityType];
  let t7;
  if ($[25] !== t6) {
    t7 = <p className="mb-3 mt-0.5 text-xs text-on-surface-variant leading-snug">{t6}</p>;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== codeString || $[28] !== otp) {
    t8 = !otp.otpSent ? <form className="space-y-3" onSubmit={e => {
      e.preventDefault();
      if (!otp.email || otp.isSending) {
        return;
      }
      otp.submitEmail();
    }}><label className="block"><span className="block text-[10px] font-bold text-on-surface-variant tracking-widest uppercase mb-1">E-Mail-Adresse</span><input type="email" inputMode="email" autoComplete="email" value={otp.email} onChange={e_0 => otp.setEmail(e_0.target.value)} disabled={otp.isSending} placeholder="ihre@email.de" className="w-full rounded-lg border border-surface-container bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary" /></label><button type="submit" disabled={!otp.email || otp.isSending} className={cn("w-full rounded-full py-2.5 text-sm font-bold transition-all active:scale-95", !otp.email || otp.isSending ? "bg-surface-container text-on-surface-variant cursor-not-allowed" : "bg-secondary-fixed text-on-secondary-fixed hover:opacity-90 shadow-md shadow-secondary-fixed/20")}>{otp.isSending ? "Wird versendet \u2026" : "Best\xE4tigungscode senden"}</button>{otp.error && <p className="text-xs text-error">{otp.error}</p>}</form> : <form className="space-y-3" onSubmit={e_1 => {
      e_1.preventDefault();
      if (codeString.length < 6 || otp.isLoading) {
        return;
      }
      otp.verifyOtp();
    }}><p className="text-xs text-on-surface-variant leading-snug">Code für <span className="font-semibold break-all">{otp.email}</span> ·{" "}<button type="button" onClick={otp.startEditingEmail} disabled={otp.isSending} className="underline hover:no-underline disabled:opacity-50">E-Mail ändern</button></p><OtpDeliveryNotice status={otp.deliveryStatus} showRecovery={otp.showDeliveryRecovery} /><label className="block"><span className="block text-[10px] font-bold text-on-surface-variant tracking-widest uppercase mb-1">6-stelliger Code</span><input type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={codeString} onChange={e_2 => {
          const digits = e_2.target.value.replace(/\D/g, "").slice(0, 6);
          const next = Array(6).fill("");
          for (let i = 0; i < digits.length; i++) {
            next[i] = digits[i];
          }
          otp.setCode(next);
        }} disabled={otp.isLoading} placeholder="123456" className="w-full rounded-lg border border-surface-container bg-white px-3 py-2 text-center text-lg font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-secondary disabled:opacity-50" autoFocus={true} /></label>{otp.error && <p className="text-xs text-error">{otp.error}</p>}<button type="submit" disabled={codeString.length < 6 || otp.isLoading} className={cn("w-full rounded-full py-2.5 text-sm font-bold transition-all active:scale-95", codeString.length < 6 || otp.isLoading ? "bg-surface-container text-on-surface-variant cursor-not-allowed" : "bg-secondary-fixed text-on-secondary-fixed hover:opacity-90 shadow-md shadow-secondary-fixed/20")}>{otp.isLoading ? "Wird gepr\xFCft \u2026" : "Code pr\xFCfen"}</button><button type="button" onClick={() => {
        otp.setCode(Array(6).fill(""));
        otp.sendOtp("resend");
      }} disabled={otp.cooldown > 0 || otp.isSending} className="block w-full text-left text-xs text-on-surface-variant hover:text-primary disabled:opacity-50 transition-colors">{otp.cooldown > 0 ? `Neuen Code anfordern (in ${otp.cooldown} s)` : otp.isSending ? "Wird versendet \u2026" : "Neuen Code anfordern"}</button></form>;
    $[27] = codeString;
    $[28] = otp;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t7 || $[31] !== t8) {
    t9 = <div className="rounded-2xl border border-surface-container bg-surface p-3">{t5}{t7}{t8}</div>;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
}